<div class="dashboard-page">
  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <div class="loading-card">
      <div class="spinner">
        <div class="spinner-border"></div>
      </div>
      <h3 class="loading-text">{{ 'DASHBOARD.LOADING.TITLE' | translate }}</h3>
      <p class="loading-subtext">{{ 'DASHBOARD.LOADING.SUBTITLE' | translate }}</p>
    </div>
  </div>

  <!-- Dashboard Content -->
  <div class="dashboard-content" [hidden]="isLoading">
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Organization Overview Section -->
      <section class="section organization-overview">
        <div class="section-header">
          <div class="section-title">
            <i class="pi pi-building"></i>
            <h2>{{ 'DASHBOARD.ORGANIZATION_OVERVIEW.TITLE' | translate }}</h2>
          </div>
        </div>

        <div class="stats-grid">
          <!-- Total Learners -->
          <div class="stat-card learners">
            <div class="stat-header">
              <span class="stat-label">{{ 'DASHBOARD.STATS.TOTAL_LEARNERS' | translate }}</span>
              <div class="stat-icon">
                <i class="pi pi-users"></i>
              </div>
            </div>
            <div class="stat-value">{{ dashboardStats?.overview?.totalLearners || basicStats?.learners || 0 | number }}
            </div>
            <div class="stat-details">
              <span class="detail active">{{ dashboardStats?.overview?.activeLearners || 0 | number }} {{
                'DASHBOARD.STATS.ACTIVE' | translate }}</span>
              <span class="detail-divider">|</span>
              <span class="detail inactive">{{ dashboardStats?.overview?.inactiveLearners || 0 | number }} {{
                'DASHBOARD.STATS.INACTIVE' | translate }}</span>
            </div>
            <div class="stat-trend positive" *ngIf="dashboardStats?.trends?.learnerGrowth?.length">
              <i class="pi pi-arrow-up"></i>
              <span>+{{ dashboardStats?.trends?.learnerGrowth[dashboardStats.trends.learnerGrowth.length - 1]?.change |
                number:'1.0-0' }}% {{ 'DASHBOARD.STATS.GROWTH' | translate }}</span>
            </div>
          </div>

          <!-- Total Staff -->
          <div class="stat-card staff">
            <div class="stat-header">
              <span class="stat-label">{{ 'DASHBOARD.STATS.TOTAL_STAFF' | translate }}</span>
              <div class="stat-icon">
                <i class="pi pi-id-card"></i>
              </div>
            </div>
            <div class="stat-value">{{ dashboardStats?.overview?.totalStaff || basicStats?.staff?.length || 0 }}</div>
            <div class="stat-details">
              <span class="detail active">{{ dashboardStats?.staff?.activeStaff || 0 }} {{ 'DASHBOARD.STATS.ACTIVE' |
                translate }}</span>
              <span class="detail-divider">|</span>
              <span class="detail on-leave">{{ (dashboardStats?.overview?.totalStaff -
                dashboardStats?.staff?.activeStaff) || 0 }} {{ 'DASHBOARD.STATS.ON_LEAVE' | translate }}</span>
            </div>
            <div class="stat-trend positive" *ngIf="dashboardStats?.recentActivity?.newStaff?.length">
              <i class="pi pi-arrow-up"></i>
              <span>+{{ dashboardStats?.recentActivity?.newStaff?.length }} {{ 'DASHBOARD.STATS.NEW_HIRES' | translate
                }}</span>
            </div>
          </div>

          <!-- Resources -->
          <div class="stat-card resources">
            <div class="stat-header">
              <span class="stat-label">{{ 'DASHBOARD.STATS.RESOURCES' | translate }}</span>
              <div class="stat-icon">
                <i class="pi pi-th-large"></i>
              </div>
            </div>
            <div class="stat-value">{{ dashboardStats?.overview?.totalClassrooms || basicStats?.classrooms || 0 }}</div>
            <div class="stat-details">
              <span class="detail">{{ 'DASHBOARD.STATS.CLASSROOMS' | translate }}</span>
              <span class="detail count">{{ dashboardStats?.overview?.totalClassrooms || 0 }}</span>
              <span class="detail-divider">|</span>
              <span class="detail">{{ 'DASHBOARD.STATS.DEPARTMENTS' | translate }}</span>
              <span class="detail count">{{ dashboardStats?.overview?.totalDepartments || 0 }}</span>
            </div>
          </div>

          <!-- Total Courses -->
          <div class="stat-card courses">
            <div class="stat-header">
              <span class="stat-label">{{ 'DASHBOARD.STATS.TOTAL_COURSES' | translate }}</span>
              <div class="stat-icon">
                <i class="pi pi-book"></i>
              </div>
            </div>
            <div class="stat-value">{{ dashboardStats?.overview?.totalCourses || 0 }}</div>
            <div class="stat-details">
              <span class="detail enrollment">{{ dashboardStats?.academic?.resultsPublicationRate || 85 | number:'1.0-0'
                }}% {{ 'DASHBOARD.STATS.ACTIVE_ENROLLMENT' | translate }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Financial Overview Section -->
      <section class="section financial-overview" *ngIf="isAdmin || isClient">
        <div class="section-header">
          <div class="section-title">
            <i class="pi pi-dollar"></i>
            <h2>{{ 'DASHBOARD.FINANCIAL_OVERVIEW.TITLE' | translate }}</h2>
          </div>
          <div class="section-actions">
            <p-dropdown [options]="[{label: 'This Month', value: 'month'}, {label: 'This Year', value: 'year'}]"
              [(ngModel)]="financialPeriod" optionLabel="label" optionValue="value" styleClass="period-dropdown">
            </p-dropdown>
          </div>
        </div>

        <div class="financial-grid">
          <!-- Financial Metrics -->
          <div class="financial-metrics">
            <div class="metric-card total-expected">
              <span class="metric-label">{{ 'DASHBOARD.FINANCIAL_OVERVIEW.TOTAL_EXPECTED' | translate }}</span>
              <span class="metric-value">{{ formatCurrency(dashboardStats?.financials?.totalFeesExpected ||
                (feeStatistics?.totalCollected + feeStatistics?.totalDue + feeStatistics?.totalRemaining)) }}</span>
            </div>
            <div class="metric-card collected">
              <span class="metric-label">{{ 'DASHBOARD.FINANCIAL_OVERVIEW.COLLECTED' | translate }}</span>
              <span class="metric-value">{{ formatCurrency(dashboardStats?.financials?.totalFeesCollected ||
                feeStatistics?.totalCollected) }}</span>
              <div class="metric-progress">
                <div class="progress-bar" [style.width.%]="getCollectionRate()"></div>
              </div>
              <span class="metric-rate">{{ getCollectionRate() | number:'1.1-1' }}% Rate</span>
            </div>
            <div class="metric-card outstanding">
              <span class="metric-label">{{ 'DASHBOARD.FINANCIAL_OVERVIEW.OUTSTANDING' | translate }}</span>
              <span class="metric-value">{{ formatCurrency(dashboardStats?.financials?.totalFeesOutstanding ||
                feeStatistics?.totalRemaining) }}</span>
            </div>
          </div>

          <!-- Monthly Revenue Chart -->
          <div class="revenue-chart-container">
            <h3 class="chart-title">{{ 'DASHBOARD.FINANCIAL_OVERVIEW.MONTHLY_REVENUE_TREND' | translate }}</h3>
            <div class="chart-wrapper">
              <div class="chart-bars">
                <div class="bar-group" *ngFor="let revenue of dashboardStats?.financials?.monthlyRevenue">
                  <div class="bar-container">
                    <div class="bar" [style.height.%]="getRevenueBarHeight(revenue?.amount)"></div>
                  </div>
                  <span class="bar-label">{{ revenue?.month }}</span>
                </div>
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-dot card"></span>
                  <span>Card ({{ dashboardStats?.financials?.paymentMethodBreakdown?.['Credit Card']?.percentage || 60
                    }}%)</span>
                </div>
                <div class="legend-item">
                  <span class="legend-dot bank"></span>
                  <span>Bank ({{ dashboardStats?.financials?.paymentMethodBreakdown?.['Bank Transfer']?.percentage || 40
                    }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Transactions -->
        <div class="transactions-section">
          <h3 class="section-subtitle">{{ 'DASHBOARD.RECENT_TRANSACTIONS.TITLE' | translate }}</h3>
          <div class="transactions-table-wrapper">
            <table class="transactions-table">
              <thead>
                <tr>
                  <th>{{ 'DASHBOARD.RECENT_TRANSACTIONS.STUDENT' | translate }}</th>
                  <th>{{ 'DASHBOARD.RECENT_TRANSACTIONS.ID' | translate }}</th>
                  <th>{{ 'DASHBOARD.RECENT_TRANSACTIONS.METHOD' | translate }}</th>
                  <th>{{ 'DASHBOARD.RECENT_TRANSACTIONS.STATUS' | translate }}</th>
                  <th>{{ 'DASHBOARD.RECENT_TRANSACTIONS.AMOUNT' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let payment of (dashboardStats?.financials?.recentPayments || feeStatistics?.recentTransactions)?.slice(0, 5); let i = index">
                  <td class="student-cell">
                    <span class="student-name">{{ payment.learnerName }}</span>
                  </td>
                  <td class="id-cell">#ST-{{ today.getFullYear() }}-{{ (i + 1).toString().padStart(3, '0') }}</td>
                  <td class="method-cell">{{ payment.paymentMethod }}</td>
                  <td class="status-cell">
                    <span class="status-badge" [ngClass]="getStatusClass(payment.status)">{{ payment.status }}</span>
                  </td>
                  <td class="amount-cell">{{ formatCurrency(payment.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Bottom Grid: Academics & Assignment Stats -->
      <div class="bottom-grid">
        <!-- Academics Section -->
        <section class="section academics-section">
          <div class="section-header">
            <div class="section-title">
              <i class="pi pi-graduation-cap"></i>
              <h2>{{ 'DASHBOARD.ACADEMICS.TITLE' | translate }}</h2>
            </div>
            <a class="section-link" routerLink="/app/results">{{ 'DASHBOARD.ACADEMICS.FULL_REPORT' | translate }}</a>
          </div>

          <div class="academics-content">
            <div class="academics-metrics">
              <div class="academic-metric avg-score">
                <span class="metric-label">{{ 'DASHBOARD.ACADEMICS.AVG_CLASS_SCORE' | translate }}</span>
                <span class="metric-value">{{ (dashboardStats?.academic?.averageClassPerformance * 5) || 78.4 |
                  number:'1.1-1' }}%</span>
                <span class="metric-trend positive">
                  <i class="pi pi-arrow-up"></i> +2%
                </span>
              </div>
              <div class="academic-metric results-published">
                <span class="metric-label">{{ 'DASHBOARD.ACADEMICS.RESULTS_PUBLISHED' | translate }}</span>
                <span class="metric-value">{{ dashboardStats?.academic?.resultsPublicationRate || 92 | number:'1.0-0'
                  }}%</span>
                <span class="metric-note">This Term</span>
              </div>
            </div>

            <div class="top-classes">
              <h4>{{ 'DASHBOARD.ACADEMICS.TOP_PERFORMING_CLASSES' | translate }}</h4>
              <div class="class-list">
                <div class="class-item"
                  *ngFor="let cls of dashboardStats?.academic?.topPerformingClasses; let i = index">
                  <span class="class-name">{{ cls.className }}</span>
                  <div class="class-bar-wrapper">
                    <div class="class-bar" [style.width.%]="(cls.averageScore / 20) * 100"
                      [ngClass]="getPerformanceBarClass(i)"></div>
                  </div>
                  <span class="class-score">{{ (cls.averageScore / 20 * 100) | number:'1.0-0' }}%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Assignment Stats Section -->
        <section class="section assignment-section">
          <div class="section-header">
            <div class="section-title">
              <i class="pi pi-file-edit"></i>
              <h2>{{ 'DASHBOARD.ASSIGNMENTS.TITLE' | translate }}</h2>
            </div>
            <span class="total-badge">{{ 'DASHBOARD.ASSIGNMENTS.TOTAL' | translate }}: {{
              dashboardStats?.assignments?.totalAssignments || 450 }}</span>
          </div>

          <div class="assignment-content">
            <div class="assignment-metrics">
              <div class="assignment-metric active">
                <span class="metric-label">{{ 'DASHBOARD.ASSIGNMENTS.ACTIVE' | translate }}</span>
                <span class="metric-value">{{ dashboardStats?.assignments?.activeAssignments || 120 }}</span>
              </div>
              <div class="assignment-metric pending">
                <span class="metric-label">{{ 'DASHBOARD.ASSIGNMENTS.PENDING' | translate }}</span>
                <span class="metric-value">{{ dashboardStats?.assignments?.pendingAssignments || 45 }}</span>
              </div>
              <div class="assignment-metric overdue">
                <span class="metric-label">{{ 'DASHBOARD.ASSIGNMENTS.OVERDUE' | translate }}</span>
                <span class="metric-value">{{ dashboardStats?.assignments?.overdueAssignments || 12 }}</span>
              </div>
            </div>

            <div class="assignment-progress">
              <input type="range" class="progress-slider"
                [value]="dashboardStats?.assignments?.averageCompletionRate || 82" min="0" max="100" disabled>
            </div>

            <div class="completion-rate">
              <span class="rate-label">{{ 'DASHBOARD.ASSIGNMENTS.AVG_COMPLETION_RATE' | translate }}</span>
              <span class="rate-value">{{ dashboardStats?.assignments?.averageCompletionRate || 82 }}%</span>
            </div>
            <p class="latest-assignment" *ngIf="dashboardStats?.assignments?.recentAssignments?.length">
              Latest: "{{ dashboardStats?.assignments?.recentAssignments[0]?.name }}" ({{
              dashboardStats?.assignments?.recentAssignments[0]?.courseName }}) due in 2 days.
            </p>
          </div>
        </section>
      </div>

      <!-- Recent System Activity -->
      <section class="section activity-section">
        <h3 class="section-subtitle">{{ 'DASHBOARD.ACTIVITY.TITLE' | translate }}</h3>
        <div class="activity-grid">
          <div class="activity-card enrollment" *ngIf="dashboardStats?.recentActivity?.newLearners?.length">
            <div class="activity-icon">
              <i class="pi pi-user-plus"></i>
            </div>
            <div class="activity-content">
              <h4>{{ 'DASHBOARD.ACTIVITY.NEW_LEARNER_ENROLLMENT' | translate }}</h4>
              <p>{{ dashboardStats?.recentActivity?.newLearners?.length }} new students added to {{
                dashboardStats?.recentActivity?.newLearners[0]?.classroomName }}.</p>
              <span class="activity-time">15 mins ago</span>
            </div>
          </div>

          <div class="activity-card results" *ngIf="dashboardStats?.recentActivity?.recentResults?.length">
            <div class="activity-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <div class="activity-content">
              <h4>{{ 'DASHBOARD.ACTIVITY.RESULTS_PUBLISHED' | translate }}</h4>
              <p>Final exams results for {{ dashboardStats?.recentActivity?.recentResults[0]?.className }} are live.</p>
              <span class="activity-time">2 hours ago</span>
            </div>
          </div>

          <div class="activity-card alert" *ngIf="dashboardStats?.systemHealth?.alerts?.length">
            <div class="activity-icon">
              <i class="pi pi-exclamation-triangle"></i>
            </div>
            <div class="activity-content">
              <h4>{{ 'DASHBOARD.ACTIVITY.SYSTEM_ALERT' | translate }}</h4>
              <p>{{ dashboardStats?.systemHealth?.alerts[0]?.message }}</p>
              <span class="activity-time">Just now</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Right Sidebar -->
    <aside class="sidebar">
      <!-- System Health -->
      <div class="sidebar-card system-health">
        <div class="card-header">
          <i class="pi pi-server"></i>
          <h3>{{ 'DASHBOARD.SYSTEM_HEALTH.TITLE' | translate }}</h3>
        </div>
        <div class="card-content">
          <div class="health-item">
            <span class="health-label">{{ 'DASHBOARD.SYSTEM_HEALTH.DATA_COMPLETENESS' | translate }}</span>
            <div class="health-bar-wrapper">
              <div class="health-bar"
                [style.width.%]="dashboardStats?.systemHealth?.dataCompleteness?.learnersWithResults || 88"></div>
            </div>
            <span class="health-value">{{ dashboardStats?.systemHealth?.dataCompleteness?.learnersWithResults || 88
              }}%</span>
          </div>
          <div class="health-item">
            <span class="health-label">{{ 'DASHBOARD.SYSTEM_HEALTH.SERVER_UPTIME' | translate }}</span>
            <div class="health-bar-wrapper">
              <div class="health-bar success" [style.width.%]="99.9"></div>
            </div>
            <span class="health-value success">99.9%</span>
          </div>
        </div>
      </div>

      <!-- Calendar -->
      <div class="sidebar-card calendar">
        <div class="card-header">
          <h3>{{ getFormattedMonth() }}</h3>
          <div class="calendar-nav">
            <button class="nav-btn"><i class="pi pi-chevron-left"></i></button>
            <button class="nav-btn"><i class="pi pi-chevron-right"></i></button>
          </div>
        </div>
        <div class="card-content">
          <div class="calendar-grid">
            <div class="weekdays">
              <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
            </div>
            <div class="days">
              <div
                *ngFor="let day of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31]"
                class="day" [ngClass]="{'today': day === todayDate}">
                {{ day }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pending Approvals -->
      <div class="sidebar-card pending-approvals">
        <div class="card-header">
          <h3>{{ 'DASHBOARD.PENDING_APPROVALS.TITLE' | translate }}</h3>
        </div>
        <div class="card-content">
          <div class="approval-item leave">
            <div class="approval-indicator"></div>
            <div class="approval-content">
              <div class="approval-header">
                <h4>{{ 'DASHBOARD.PENDING_APPROVALS.LEAVE_REQUEST' | translate }}</h4>
                <span class="approval-time">1h ago</span>
              </div>
              <p>John Doe (Math Teacher) • Sick Leave</p>
              <div class="approval-actions">
                <button class="btn-approve">{{ 'COMMON.APPROVE' | translate }}</button>
                <button class="btn-deny">{{ 'COMMON.DENY' | translate }}</button>
              </div>
            </div>
          </div>

          <div class="approval-item syllabus">
            <div class="approval-indicator"></div>
            <div class="approval-content">
              <div class="approval-header">
                <h4>{{ 'DASHBOARD.PENDING_APPROVALS.COURSE_SYLLABUS' | translate }}</h4>
                <span class="approval-time">3h ago</span>
              </div>
              <p>Review new Python 102 curriculum.</p>
              <div class="approval-actions">
                <button class="btn-review">{{ 'DASHBOARD.PENDING_APPROVALS.REVIEW_DOCUMENT' | translate }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </div>
</div>